import type {
  CGConstraint,
  CGDrawable,
  CGPoint,
  CGValue,
  CGVariable,
  CgMacroKind,
  ConstrainedGeometryBlock,
} from '@jeraisonne/ai';
import { CG_MACROS, deriveSelectors, expandMacro } from '@jeraisonne/ai';
import { ConstrainedGeometryBlockRenderer, solveConstrainedGeometry } from '@jeraisonne/ui';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

const INPUT =
  'rounded border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400';
const SECTION = 'text-[11px] font-semibold uppercase tracking-wide text-slate-600';

let ctr = 0;
const uid = (p: string) => {
  ctr += 1;
  return `${p}${ctr}`;
};

/** "5" → 5 ; "2*x+1" → "2*x+1" (expression). */
function parseCGValue(raw: string): CGValue {
  const t = raw.trim();
  if (t !== '' && !Number.isNaN(Number(t))) return Number(t);
  return t;
}

/**
 * Résout une liste « A, B, C » (noms affichés OU ids) en IDs de points — le
 * solveur référence les points par ID, pas par nom (qui peuvent différer pour
 * les points créés par macro : id `p1`, nom `A`). Sans ça, collinear/polygon
 * échouent silencieusement.
 */
export function parsePointRefs(raw: string, points: CGPoint[]): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((tok) => points.find((p) => (p.name ?? p.id) === tok)?.id ?? tok);
}
/** IDs → texte lisible en noms (« A, B, C »). */
export function pointRefsToText(ids: string[], points: CGPoint[]): string {
  return ids.map((id) => points.find((p) => p.id === id)?.name ?? id).join(', ');
}

/** Résolveur de référence : un nom de point → son id ; un id reste tel quel. */
function makeRefResolver(points: CGPoint[]): (ref: string) => string {
  const ids = new Set(points.map((p) => p.id));
  const idByName = new Map<string, string>();
  for (const p of points) if (p.name) idByName.set(p.name, p.id);
  return (ref) => (ids.has(ref) ? ref : (idByName.get(ref) ?? ref));
}

function normConstraint(c: CGConstraint, rid: (r: string) => string): CGConstraint {
  switch (c.type) {
    case 'distance':
      return { ...c, a: rid(c.a), b: rid(c.b) };
    case 'angle':
      return { ...c, vertex: rid(c.vertex), a: rid(c.a), b: rid(c.b) };
    case 'collinear':
      return { ...c, points: c.points.map(rid) };
    case 'on_line':
    case 'midpoint':
      return { ...c, point: rid(c.point), a: rid(c.a), b: rid(c.b) };
    case 'parallel':
    case 'perpendicular':
    case 'equal_length':
      return { ...c, a: rid(c.a), b: rid(c.b), c: rid(c.c), d: rid(c.d) };
    case 'on_circle':
      return { ...c, point: rid(c.point), center: rid(c.center) };
    case 'on_function':
      return { ...c, point: rid(c.point) };
    case 'orientation':
      return { ...c, a: rid(c.a), b: rid(c.b) };
  }
}

function normDrawable(d: CGDrawable, rid: (r: string) => string): CGDrawable {
  switch (d.kind) {
    case 'segment':
    case 'line':
      return { ...d, a: rid(d.a), b: rid(d.b) };
    case 'circle':
      return { ...d, center: rid(d.center) };
    case 'polygon':
      return { ...d, points: d.points.map(rid) };
    case 'angle':
      return { ...d, vertex: rid(d.vertex), a: rid(d.a), b: rid(d.b) };
    case 'function':
      return d;
  }
}

/**
 * Normalise toutes les références de points (contraintes + tracés) en IDs —
 * soigne les données où une référence est un NOM (créée via champ texte) alors
 * que l'id du point diffère. Sans ça, la contrainte vise un point inexistant et
 * est ignorée silencieusement par le solveur.
 */
export function normalizeRefs(block: ConstrainedGeometryBlock): ConstrainedGeometryBlock {
  const rid = makeRefResolver(block.points);
  return {
    ...block,
    constraints: block.constraints.map((c) => normConstraint(c, rid)),
    drawables: block.drawables.map((d) => normDrawable(d, rid)),
  };
}

/** Tous les points (ids OU noms) référencés par une contrainte. */
function refsOfConstraint(c: CGConstraint): string[] {
  switch (c.type) {
    case 'distance':
      return [c.a, c.b];
    case 'angle':
      return [c.vertex, c.a, c.b];
    case 'collinear':
      return c.points;
    case 'on_line':
    case 'midpoint':
      return [c.point, c.a, c.b];
    case 'parallel':
    case 'perpendicular':
    case 'equal_length':
      return [c.a, c.b, c.c, c.d];
    case 'on_circle':
      return [c.point, c.center];
    case 'on_function':
      return [c.point];
    case 'orientation':
      return [c.a, c.b];
  }
}

/** Tous les points (ids OU noms) référencés par un tracé. */
function refsOfDrawable(d: CGDrawable): string[] {
  switch (d.kind) {
    case 'segment':
    case 'line':
      return [d.a, d.b];
    case 'circle':
      return [d.center];
    case 'polygon':
      return d.points;
    case 'angle':
      return [d.vertex, d.a, d.b];
    case 'function':
      return [];
  }
}

/**
 * Supprime un groupe-forme : retire ses contraintes et ses tracés, puis ses points
 * — SAUF un point encore référencé par un élément survivant (cas « sommet partagé »
 * avec une autre forme/contrainte ajoutée à la main). Logique pure (exportée pour test).
 */
export function removeGroupFrom(
  block: ConstrainedGeometryBlock,
  groupId: string,
): ConstrainedGeometryBlock {
  const constraints = block.constraints.filter((c) => c.groupId !== groupId);
  const drawables = block.drawables.filter((d) => d.groupId !== groupId);
  const referenced = new Set<string>();
  for (const c of constraints) for (const r of refsOfConstraint(c)) referenced.add(r);
  for (const d of drawables) for (const r of refsOfDrawable(d)) referenced.add(r);
  const points = block.points.filter(
    (p) =>
      p.groupId !== groupId || referenced.has(p.id) || (p.name != null && referenced.has(p.name)),
  );
  const groups = (block.groups ?? []).filter((g) => g.id !== groupId);
  return { ...block, points, constraints, drawables, groups };
}

function PointSelect({
  points,
  value,
  onChange,
  label,
  className,
}: {
  points: CGPoint[];
  value: string;
  onChange: (id: string) => void;
  label?: string;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className ? `${INPUT} ${className}` : INPUT}
      title={label}
      aria-label={label}
    >
      {points.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name || p.id}
        </option>
      ))}
    </select>
  );
}

const CONSTRAINT_TYPES: CGConstraint['type'][] = [
  'distance',
  'angle',
  'collinear',
  'on_line',
  'parallel',
  'perpendicular',
  'on_circle',
  'midpoint',
  'equal_length',
  'on_function',
  'orientation',
];

const DRAWABLE_KINDS: CGDrawable['kind'][] = [
  'segment',
  'line',
  'circle',
  'polygon',
  'angle',
  'function',
];

export function ConstrainedGeometryForm({
  block,
  onChange,
}: {
  block: ConstrainedGeometryBlock;
  onChange: (next: ConstrainedGeometryBlock) => void;
}) {
  const pts = block.points;
  const firstId = pts[0]?.id ?? 'A';

  // Applique le solveur À LA DEMANDE (validation explicite) plutôt qu'à chaque
  // édition : évite les états instables quand une contrainte est à moitié saisie.
  const recompute = () => {
    // Soigne d'abord les références (noms→ids) puis résout — garantit que toutes
    // les contraintes visent des points existants (sinon ignorées silencieusement).
    const norm = normalizeRefs(block);
    const scope: Record<string, number> = {};
    for (const v of norm.variables ?? []) scope[v.name.toLowerCase()] = v.value;
    if (norm.parameter) scope[norm.parameter.name.toLowerCase()] = norm.parameter.value;
    onChange({
      ...norm,
      points: solveConstrainedGeometry(norm.points, norm.constraints, scope),
    });
  };

  // ── Formes (macros) ─────────────────────────────────────────────────────────
  // Insère une figure pédagogique (points + contraintes intrinsèques + tracés)
  // en un clic, avec des IDs et noms de sommets frais. Voir @jeraisonne/ai/cg-macros.
  const addMacro = (kind: CgMacroKind) => {
    const existing = {
      ids: new Set<string>([
        ...block.points.map((p) => p.id),
        ...block.constraints.map((c) => c.id),
        ...block.drawables.map((d) => d.id),
        ...(block.groups ?? []).map((g) => g.id),
      ]),
      names: new Set<string>(block.points.map((p) => p.name ?? p.id)),
    };
    // Décale la nouvelle forme à droite de l'emprise existante (pas de superposition).
    const xs = block.points.map((p) => p.x);
    const offset = xs.length ? { x: Math.max(...xs) + 5, y: 0 } : { x: 0, y: 0 };
    const frag = expandMacro(kind, existing, { offset });

    // Une « forme » (def.group === 'forme') devient un GROUPE supprimable d'un bloc :
    // ses points/contraintes/tracés sont tagués d'un même groupId, et un registre
    // `groups` garde son libellé. Les primitives (point/segment) restent indépendantes.
    const def = CG_MACROS.find((m) => m.kind === kind);
    if (def?.group === 'forme') {
      const groupId = uid('g');
      const label = `${def.label} ${frag.points.map((p) => p.name ?? p.id).join('')}`.trim();
      onChange({
        ...block,
        points: [...block.points, ...frag.points.map((p) => ({ ...p, groupId }))],
        constraints: [...block.constraints, ...frag.constraints.map((c) => ({ ...c, groupId }))],
        drawables: [...block.drawables, ...frag.drawables.map((d) => ({ ...d, groupId }))],
        groups: [...(block.groups ?? []), { id: groupId, kind, label }],
      });
      return;
    }
    onChange({
      ...block,
      points: [...block.points, ...frag.points],
      constraints: [...block.constraints, ...frag.constraints],
      drawables: [...block.drawables, ...frag.drawables],
    });
  };

  // ── Groupes-formes ───────────────────────────────────────────────────────────
  const groups = block.groups ?? [];
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroupOpen = (id: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const removeGroup = (id: string) => onChange(removeGroupFrom(block, id));
  const updateGroupLabel = (id: string, label: string) =>
    onChange({ ...block, groups: groups.map((g) => (g.id === id ? { ...g, label } : g)) });

  // ── Interaction élève (policy) ───────────────────────────────────────────────
  const policy = block.interactionPolicy;
  const selectors = deriveSelectors(block);
  const setPolicy = (patch: Partial<NonNullable<ConstrainedGeometryBlock['interactionPolicy']>>) =>
    onChange({ ...block, interactionPolicy: { ...policy, ...patch } });

  const toggleDraggable = (id: string, checked: boolean) => {
    const current = policy?.draggablePoints ?? pts.filter((p) => !p.fixed).map((p) => p.id);
    const next = checked ? [...new Set([...current, id])] : current.filter((x) => x !== id);
    // Rendre un point déplaçable par l'élève implique qu'il n'est plus « fixe »
    // (sinon il ne pourrait jamais bouger). On défixe donc à la coche.
    const points =
      checked && block.points.some((p) => p.id === id && p.fixed)
        ? block.points.map((p) => (p.id === id ? { ...p, fixed: false } : p))
        : block.points;
    onChange({ ...block, points, interactionPolicy: { ...policy, draggablePoints: next } });
  };

  const sameSeg = (x: [string, string], y: [string, string]) =>
    (x[0] === y[0] && x[1] === y[1]) || (x[0] === y[1] && x[1] === y[0]);
  const toggleSegMeasure = (a: string, b: string, checked: boolean) => {
    const segs = policy?.measures?.segments ?? [];
    const next = checked
      ? [...segs, [a, b] as [string, string]]
      : segs.filter((s) => !sameSeg(s, [a, b]));
    setPolicy({ measures: { ...policy?.measures, segments: next } });
  };

  const sameAng = (x: [string, string, string], y: [string, string, string]) =>
    x[1] === y[1] && ((x[0] === y[0] && x[2] === y[2]) || (x[0] === y[2] && x[2] === y[0]));
  const toggleAngleMeasure = (a: string, v: string, b: string, checked: boolean) => {
    const angs = policy?.measures?.angles ?? [];
    const next = checked
      ? [...angs, [a, v, b] as [string, string, string]]
      : angs.filter((g) => !sameAng(g, [a, v, b]));
    setPolicy({ measures: { ...policy?.measures, angles: next } });
  };

  // ── Points ────────────────────────────────────────────────────────────────
  const addPoint = () => {
    const id = uid('P');
    onChange({ ...block, points: [...pts, { id, name: id, x: 0, y: 0 }] });
  };
  const updatePoint = (i: number, patch: Partial<CGPoint>) =>
    onChange({ ...block, points: pts.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const removePoint = (i: number) => onChange({ ...block, points: pts.filter((_, j) => j !== i) });

  // ── Contraintes ────────────────────────────────────────────────────────────
  const addConstraint = () =>
    onChange({
      ...block,
      constraints: [
        ...block.constraints,
        { id: uid('c'), type: 'distance', a: firstId, b: pts[1]?.id ?? firstId, value: 1 },
      ],
    });
  const updateConstraint = (i: number, next: CGConstraint) =>
    onChange({ ...block, constraints: block.constraints.map((c, j) => (j === i ? next : c)) });
  const removeConstraint = (i: number) =>
    onChange({ ...block, constraints: block.constraints.filter((_, j) => j !== i) });

  const changeConstraintType = (i: number, type: CGConstraint['type']) => {
    const prev = block.constraints[i]!;
    const id = prev.id;
    const a = firstId;
    const b = pts[1]?.id ?? firstId;
    let next: CGConstraint;
    switch (type) {
      case 'distance':
      case 'equal_length':
        next = type === 'distance' ? { id, type, a, b, value: 1 } : { id, type, a, b, c: a, d: b };
        break;
      case 'angle':
        next = { id, type, vertex: a, a: b, b: pts[2]?.id ?? b, value: 90 };
        break;
      case 'collinear':
        next = { id, type, points: pts.slice(0, 3).map((p) => p.id), ordered: true };
        break;
      case 'on_line':
        next = { id, type, point: a, a: b, b: pts[2]?.id ?? b };
        break;
      case 'parallel':
      case 'perpendicular':
        next = { id, type, a, b, c: a, d: b };
        break;
      case 'on_circle':
        next = { id, type, point: a, center: b, radius: 1 };
        break;
      case 'midpoint':
        next = { id, type, point: a, a: b, b: pts[2]?.id ?? b };
        break;
      case 'on_function':
        next = { id, type, point: a, expr: 'x^2' };
        break;
      case 'orientation':
        next = { id, type, a, b, vx: 1, vy: 0 };
        break;
    }
    // Conserve l'appartenance au groupe-forme et l'état actif/inactif au changement de type.
    updateConstraint(i, { ...next, groupId: prev.groupId, enabled: prev.enabled });
  };

  // ── Tracés ──────────────────────────────────────────────────────────────────
  const addDrawable = () =>
    onChange({
      ...block,
      drawables: [
        ...block.drawables,
        { id: uid('d'), kind: 'segment', a: firstId, b: pts[1]?.id ?? firstId },
      ],
    });
  const updateDrawable = (i: number, next: CGDrawable) =>
    onChange({ ...block, drawables: block.drawables.map((d, j) => (j === i ? next : d)) });
  const removeDrawable = (i: number) =>
    onChange({ ...block, drawables: block.drawables.filter((_, j) => j !== i) });

  // ── Variables (curseurs) ─────────────────────────────────────────────────────
  const vars = block.variables ?? [];
  const VAR_NAMES = ['a', 'b', 'k', 't', 'm', 'n', 'p', 'q'];
  const nextVarName = () => {
    const used = new Set(vars.map((v) => v.name.toLowerCase()));
    return VAR_NAMES.find((n) => !used.has(n)) ?? `v${vars.length + 1}`;
  };
  const addVariable = () =>
    onChange({
      ...block,
      variables: [...vars, { name: nextVarName(), value: 1, min: 0, max: 10, step: 0.5 }],
    });
  const updateVariable = (i: number, patch: Partial<CGVariable>) =>
    onChange({ ...block, variables: vars.map((v, j) => (j === i ? { ...v, ...patch } : v)) });
  const removeVariable = (i: number) =>
    onChange({ ...block, variables: vars.filter((_, j) => j !== i) });

  // Ligne d'édition d'une contrainte — réutilisée dans la liste « Contraintes »
  // (hors-groupe) ET dans les cartes de formes (contraintes membres). `i` est
  // l'index RÉEL dans `block.constraints` (pas l'index filtré).
  const constraintRow = (c: CGConstraint, i: number) => {
    const active = c.enabled !== false;
    return (
      <div key={c.id} className="flex flex-wrap items-center gap-1.5">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => updateConstraint(i, { ...c, enabled: e.target.checked })}
          className="h-3.5 w-3.5 shrink-0 accent-indigo-600"
          aria-label="contrainte active"
          title={
            active ? 'contrainte active (décocher pour la désactiver)' : 'contrainte désactivée'
          }
        />
        <div className={`flex flex-1 flex-wrap items-center gap-1.5 ${active ? '' : 'opacity-50'}`}>
          <select
            value={c.type}
            onChange={(e) => changeConstraintType(i, e.target.value as CGConstraint['type'])}
            className={INPUT}
          >
            {CONSTRAINT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <ConstraintFields c={c} points={pts} onChange={(next) => updateConstraint(i, next)} />
        </div>
        <button
          type="button"
          onClick={() => removeConstraint(i)}
          className="rounded p-1 text-red-500 hover:bg-red-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Aperçu interactif : le drag persiste les coordonnées.
          Fond BLANC réel (#fff littéral) — le token bg-white est translucide en thème océan. */}
      <div className="rounded-lg border border-slate-200 bg-[#ffffff] p-2 text-slate-900">
        <ConstrainedGeometryBlockRenderer
          block={block}
          autoSolve={false}
          onPointsChange={(points) => onChange({ ...block, points })}
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-400">
            Édite points et contraintes, puis applique-les à la figure.
          </p>
          <button
            type="button"
            onClick={recompute}
            className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-indigo-700 active:scale-[.98]"
          >
            Recalculer la figure
          </button>
        </div>
      </div>

      {/* Formes : insère une macro (points + contraintes + tracés) en un clic */}
      <div>
        <p className={SECTION}>Ajouter une forme</p>
        <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {CG_MACROS.map((m) => (
            <button
              key={m.kind}
              type="button"
              onClick={() => addMacro(m.kind)}
              className="flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-200 px-2 py-1.5 text-center text-[11px] font-semibold text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 active:scale-[0.98]"
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Points */}
      <div>
        <p className={SECTION}>Points</p>
        <div className="mt-1 space-y-1">
          {pts.map((p, i) => (
            <div key={p.id} className="flex flex-wrap items-center gap-1.5">
              <input
                value={p.name ?? ''}
                placeholder="nom"
                onChange={(e) => updatePoint(i, { name: e.target.value })}
                className={`${INPUT} w-20`}
              />
              <input
                type="number"
                value={p.x}
                placeholder="x"
                onChange={(e) => updatePoint(i, { x: Number(e.target.value) })}
                className={`${INPUT} min-w-24 flex-1`}
                title="abscisse x"
              />
              <input
                type="number"
                value={p.y}
                placeholder="y"
                onChange={(e) => updatePoint(i, { y: Number(e.target.value) })}
                className={`${INPUT} min-w-24 flex-1`}
                title="ordonnée y"
              />
              <label className="flex items-center gap-1 text-[11px] text-slate-600">
                <input
                  type="checkbox"
                  checked={!!p.fixed}
                  onChange={(e) => updatePoint(i, { fixed: e.target.checked })}
                  className="h-3.5 w-3.5 accent-indigo-600"
                />
                fixe
              </label>
              <button
                type="button"
                onClick={() => removePoint(i)}
                className="rounded p-1 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addPoint}
          className="mt-1 inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
        >
          <Plus className="h-3 w-3" /> point
        </button>
      </div>

      {/* Formes : chaque groupe = un bloc de contraintes supprimable d'un coup */}
      {groups.length > 0 && (
        <div>
          <p className={SECTION}>Formes</p>
          <div className="mt-1 space-y-1.5">
            {groups.map((g) => {
              const open = openGroups.has(g.id);
              const memberCount = block.constraints.filter((c) => c.groupId === g.id).length;
              return (
                <div key={g.id} className="rounded-lg border border-slate-200">
                  <div className="flex items-center gap-1.5 px-1.5 py-1.5">
                    <button
                      type="button"
                      onClick={() => toggleGroupOpen(g.id)}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      aria-label={open ? 'replier' : 'déplier'}
                      title={open ? 'replier' : 'déplier'}
                    >
                      {open ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <input
                      value={g.label}
                      onChange={(e) => updateGroupLabel(g.id, e.target.value)}
                      className={`${INPUT} flex-1 font-semibold`}
                      aria-label="nom de la forme"
                      title="nom de la forme"
                    />
                    <span className="shrink-0 text-[11px] text-slate-400">
                      {memberCount} contrainte{memberCount > 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeGroup(g.id)}
                      className="rounded p-1 text-red-500 hover:bg-red-50"
                      aria-label="supprimer la forme"
                      title="supprimer la forme (points, contraintes et tracés)"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {open && (
                    <div className="space-y-1 border-t border-slate-100 px-1.5 py-1.5">
                      {memberCount === 0 ? (
                        <p className="text-[11px] text-slate-400">
                          Aucune contrainte (forme libre).
                        </p>
                      ) : (
                        block.constraints.map((c, i) =>
                          c.groupId === g.id ? constraintRow(c, i) : null,
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contraintes (hors-groupe : celles d'une forme vivent dans sa carte) */}
      <div>
        <p className={SECTION}>Contraintes</p>
        <div className="mt-1 space-y-1">
          {block.constraints.map((c, i) => (c.groupId ? null : constraintRow(c, i)))}
        </div>
        <button
          type="button"
          onClick={addConstraint}
          className="mt-1 inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
        >
          <Plus className="h-3 w-3" /> contrainte
        </button>
      </div>

      {/* Tracés */}
      <div>
        <p className={SECTION}>Tracés</p>
        <div className="mt-1 space-y-1">
          {block.drawables.map((d, i) => (
            <div key={d.id} className="flex flex-wrap items-center gap-1.5">
              <select
                value={d.kind}
                onChange={(e) =>
                  updateDrawable(
                    i,
                    defaultDrawable(e.target.value as CGDrawable['kind'], d.id, pts),
                  )
                }
                className={INPUT}
              >
                {DRAWABLE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <DrawableFields d={d} points={pts} onChange={(next) => updateDrawable(i, next)} />
              <button
                type="button"
                onClick={() => removeDrawable(i)}
                className="rounded p-1 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addDrawable}
          className="mt-1 inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
        >
          <Plus className="h-3 w-3" /> tracé
        </button>
      </div>

      {/* Paramètre x */}
      <div>
        <p className={SECTION}>Paramètre x (longueurs en fonction de x)</p>
        <label className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600">
          <input
            type="checkbox"
            checked={!!block.parameter}
            onChange={(e) =>
              onChange({
                ...block,
                parameter: e.target.checked
                  ? { name: 'x', min: 0, max: 10, value: 1, step: 0.5 }
                  : undefined,
              })
            }
            className="h-3.5 w-3.5 accent-indigo-600"
          />
          activer le curseur x
        </label>
        {block.parameter && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(['min', 'max', 'value', 'step'] as const).map((k) => (
              <label key={k} className="text-[11px] text-slate-500">
                {k}
                <input
                  type="number"
                  value={block.parameter![k] ?? 0}
                  onChange={(e) =>
                    onChange({
                      ...block,
                      parameter: { ...block.parameter!, [k]: Number(e.target.value) },
                    })
                  }
                  className={`${INPUT} ml-1 w-16`}
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Variables (curseurs multiples) */}
      <div>
        <p className={SECTION}>Variables (curseurs)</p>
        <div className="mt-1 space-y-1">
          {vars.map((v, i) => (
            <div key={`var-${i}`} className="flex flex-wrap items-center gap-1.5">
              <input
                value={v.name}
                placeholder="nom"
                onChange={(e) => updateVariable(i, { name: e.target.value })}
                className={`${INPUT} w-16`}
                title="nom (minuscule : a, b, k…)"
              />
              {(['value', 'min', 'max', 'step'] as const).map((k) => (
                <label key={k} className="text-[11px] text-slate-500">
                  {k}
                  <input
                    type="number"
                    value={v[k] ?? 0}
                    onChange={(e) =>
                      updateVariable(i, { [k]: Number(e.target.value) } as Partial<CGVariable>)
                    }
                    className={`${INPUT} ml-1 w-16`}
                  />
                </label>
              ))}
              <button
                type="button"
                onClick={() => removeVariable(i)}
                className="rounded p-1 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addVariable}
          className="mt-1 inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
        >
          <Plus className="h-3 w-3" /> variable
        </button>
      </div>

      {/* Interaction élève (policy appliquée uniquement côté élève) */}
      <div>
        <p className={SECTION}>Interaction élève</p>

        <p className="mt-1 text-[11px] text-slate-500">Points déplaçables par l’élève</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {pts.map((p) => {
            // Déplaçable par l'élève ⇔ non fixe ET (dans la liste, ou tous par défaut).
            const checked =
              !p.fixed && (policy?.draggablePoints ? policy.draggablePoints.includes(p.id) : true);
            return (
              <label
                key={p.id}
                className="flex items-center gap-1 text-[11px] text-slate-600"
                title={p.fixed ? 'cocher défixera le point pour le rendre déplaçable' : undefined}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => toggleDraggable(p.id, e.target.checked)}
                  className="h-3.5 w-3.5 accent-indigo-600"
                />
                {p.name || p.id}
              </label>
            );
          })}
        </div>

        {block.parameter && (
          <label className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={policy?.showSlider !== false}
              onChange={(e) => setPolicy({ showSlider: e.target.checked })}
              className="h-3.5 w-3.5 accent-indigo-600"
            />
            curseur visible par l’élève
          </label>
        )}

        {(selectors.segments.length > 0 || selectors.angles.length > 0) && (
          <>
            <p className="mt-2 text-[11px] text-slate-500">Mesures affichées en direct</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {selectors.segments.map((s) => {
                const checked = (policy?.measures?.segments ?? []).some((m) =>
                  sameSeg(m, [s.a, s.b]),
                );
                return (
                  <label
                    key={`seg-${s.a}-${s.b}`}
                    className="flex items-center gap-1 text-[11px] text-slate-600"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleSegMeasure(s.a, s.b, e.target.checked)}
                      className="h-3.5 w-3.5 accent-indigo-600"
                    />
                    {s.label}
                  </label>
                );
              })}
              {selectors.angles.map((g) => {
                const checked = (policy?.measures?.angles ?? []).some((m) =>
                  sameAng(m, [g.a, g.vertex, g.b]),
                );
                return (
                  <label
                    key={`ang-${g.vertex}-${g.a}-${g.b}`}
                    className="flex items-center gap-1 text-[11px] text-slate-600"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleAngleMeasure(g.a, g.vertex, g.b, e.target.checked)}
                      className="h-3.5 w-3.5 accent-indigo-600"
                    />
                    {g.label}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ValueInput({ value, onChange }: { value: CGValue; onChange: (v: CGValue) => void }) {
  return (
    <input
      value={String(value)}
      placeholder="nb ou expr en x"
      onChange={(e) => onChange(parseCGValue(e.target.value))}
      className={`${INPUT} w-24`}
      title="valeur (nombre ou expression en x)"
    />
  );
}

function ConstraintFields({
  c,
  points,
  onChange,
}: {
  c: CGConstraint;
  points: CGPoint[];
  onChange: (next: CGConstraint) => void;
}) {
  const sel = (value: string, key: string) => (
    <PointSelect
      points={points}
      value={value}
      label={key}
      onChange={(id) => onChange({ ...c, [key]: id } as CGConstraint)}
    />
  );
  switch (c.type) {
    case 'distance':
      return (
        <>
          {sel(c.a, 'a')}
          {sel(c.b, 'b')}
          <ValueInput value={c.value} onChange={(value) => onChange({ ...c, value })} />
        </>
      );
    case 'angle': {
      // Notation mathématique : ∠(côté, SOMMET, côté) — le sommet est au MILIEU,
      // comme ∠BAC (vertex A). On légende chaque champ pour lever l'ambiguïté.
      const angleField = (value: string, key: 'a' | 'vertex' | 'b', caption: string) => {
        const isVertex = key === 'vertex';
        return (
          <span className="flex flex-col items-center gap-0.5">
            <PointSelect
              points={points}
              value={value}
              label={caption}
              onChange={(id) => onChange({ ...c, [key]: id } as CGConstraint)}
              className={isVertex ? 'ring-2 ring-indigo-400' : undefined}
            />
            <span className="text-[10px] leading-none text-slate-500">{caption}</span>
          </span>
        );
      };
      return (
        <>
          <span className="self-start pt-1.5 text-base leading-none text-slate-500">∠</span>
          {angleField(c.a, 'a', 'côté')}
          {angleField(c.vertex, 'vertex', 'sommet')}
          {angleField(c.b, 'b', 'côté')}
          <ValueInput value={c.value} onChange={(value) => onChange({ ...c, value })} />°
        </>
      );
    }
    case 'collinear':
      return (
        <>
          <input
            value={pointRefsToText(c.points, points)}
            placeholder="points séparés par , (ex. A, B, C)"
            onChange={(e) => onChange({ ...c, points: parsePointRefs(e.target.value, points) })}
            className={`${INPUT} min-w-40 flex-1`}
            title="points alignés (noms séparés par des virgules)"
          />
          <label
            className="flex items-center gap-1 text-[11px] text-slate-600"
            title="garde les points dans l'ordre indiqué, entre les extrémités (évite les sauts)"
          >
            <input
              type="checkbox"
              checked={c.ordered !== false}
              onChange={(e) => onChange({ ...c, ordered: e.target.checked })}
              className="h-3.5 w-3.5 accent-indigo-600"
            />
            ordre
          </label>
        </>
      );
    case 'on_line':
    case 'midpoint':
      return (
        <>
          {sel(c.point, 'point')}
          {sel(c.a, 'a')}
          {sel(c.b, 'b')}
        </>
      );
    case 'parallel':
    case 'perpendicular':
    case 'equal_length':
      return (
        <>
          {sel(c.a, 'a')}
          {sel(c.b, 'b')}
          {sel(c.c, 'c')}
          {sel(c.d, 'd')}
        </>
      );
    case 'on_circle':
      return (
        <>
          {sel(c.point, 'point')}
          {sel(c.center, 'center')}
          <ValueInput value={c.radius} onChange={(radius) => onChange({ ...c, radius })} />
        </>
      );
    case 'on_function':
      return (
        <>
          {sel(c.point, 'point')}
          <input
            value={c.expr}
            placeholder="f(x), ex. x^2"
            onChange={(e) => onChange({ ...c, expr: e.target.value })}
            className={`${INPUT} w-28`}
            title="expression f(x)"
          />
        </>
      );
    case 'orientation':
      // a→b colinéaire au vecteur (vx, vy) — orientation seule, sans longueur.
      return (
        <>
          {sel(c.a, 'a')}
          {sel(c.b, 'b')}
          <span className="text-xs text-slate-500">∥ (</span>
          <ValueInput value={c.vx} onChange={(vx) => onChange({ ...c, vx })} />
          <ValueInput value={c.vy} onChange={(vy) => onChange({ ...c, vy })} />
          <span className="text-xs text-slate-500">)</span>
        </>
      );
  }
}

function defaultDrawable(kind: CGDrawable['kind'], id: string, pts: CGPoint[]): CGDrawable {
  const a = pts[0]?.id ?? 'A';
  const b = pts[1]?.id ?? a;
  switch (kind) {
    case 'segment':
      return { id, kind, a, b };
    case 'line':
      return { id, kind, a, b };
    case 'circle':
      return { id, kind, center: a, radius: 1 };
    case 'polygon':
      return { id, kind, points: pts.slice(0, 3).map((p) => p.id) };
    case 'angle':
      return { id, kind, vertex: a, a: b, b: pts[2]?.id ?? b };
    case 'function':
      return { id, kind, expr: 'x^2' };
  }
}

function DrawableFields({
  d,
  points,
  onChange,
}: {
  d: CGDrawable;
  points: CGPoint[];
  onChange: (next: CGDrawable) => void;
}) {
  const sel = (value: string, key: string) => (
    <PointSelect
      points={points}
      value={value}
      label={key}
      onChange={(id) => onChange({ ...d, [key]: id } as CGDrawable)}
    />
  );
  // Le libellé est proposé pour les tracés qui le SUPPORTENT (segment, angle),
  // même si la clé `label` n'existe pas encore (cas des tracés créés par macro).
  const supportsLabel = d.kind === 'segment' || d.kind === 'angle';
  const labelInput = supportsLabel ? (
    <input
      value={'label' in d ? (d.label ?? '') : ''}
      placeholder="label"
      onChange={(e) => onChange({ ...d, label: e.target.value } as CGDrawable)}
      className={`${INPUT} w-24`}
    />
  ) : null;

  const kindFields = (() => {
    switch (d.kind) {
      case 'segment':
        return (
          <>
            {sel(d.a, 'a')}
            {sel(d.b, 'b')}
            {labelInput}
          </>
        );
      case 'line':
        return (
          <>
            {sel(d.a, 'a')}
            {sel(d.b, 'b')}
          </>
        );
      case 'circle':
        return (
          <>
            {sel(d.center, 'center')}
            <ValueInput value={d.radius} onChange={(radius) => onChange({ ...d, radius })} />
          </>
        );
      case 'polygon':
        return (
          <input
            value={pointRefsToText(d.points, points)}
            placeholder="points séparés par , (ex. A, B, C)"
            onChange={(e) => onChange({ ...d, points: parsePointRefs(e.target.value, points) })}
            className={`${INPUT} min-w-40 flex-1`}
            title="sommets du polygone (noms séparés par des virgules)"
          />
        );
      case 'angle':
        return (
          <>
            {sel(d.vertex, 'vertex')}
            {sel(d.a, 'a')}
            {sel(d.b, 'b')}
            {labelInput}
          </>
        );
      case 'function':
        return (
          <input
            value={d.expr}
            placeholder="f(x), ex. x^2"
            onChange={(e) => onChange({ ...d, expr: e.target.value })}
            className={`${INPUT} w-36`}
            title="expression f(x)"
          />
        );
    }
  })();

  // Style commun : couleur (contour, ou `stroke` pour le polygone) + épaisseur.
  const colorKey = d.kind === 'polygon' ? 'stroke' : 'color';
  const colorVal = (d as Record<string, unknown>)[colorKey];
  const styleControls = (
    <>
      <input
        type="color"
        value={typeof colorVal === 'string' ? colorVal : '#374151'}
        onChange={(e) => onChange({ ...d, [colorKey]: e.target.value } as CGDrawable)}
        className="h-7 w-8 shrink-0 cursor-pointer rounded border border-slate-200"
        title="couleur"
        aria-label="couleur"
      />
      <select
        value={String('strokeWidth' in d && d.strokeWidth ? d.strokeWidth : 2)}
        onChange={(e) => onChange({ ...d, strokeWidth: Number(e.target.value) } as CGDrawable)}
        className={INPUT}
        title="épaisseur du trait"
        aria-label="épaisseur"
      >
        <option value="1">fin</option>
        <option value="2">normal</option>
        <option value="3">épais</option>
        <option value="4">très épais</option>
      </select>
    </>
  );

  return (
    <>
      {kindFields}
      {styleControls}
    </>
  );
}
